import { computed, nextTick, ref, watch, onScopeDispose, type CSSProperties, type Ref } from 'vue';
import { ANIMATION_CONFIG } from '@/config/constants';
import type { AppGameState } from './useGameFlow';

/**
 * Both letter-grid roots (LetterGrid, SelectedLettersGrid) carry this attribute
 * so the handover can find whichever one a phase layer happens to render.
 */
const GRID_SELECTOR = '[data-phase-grid]';

/** The grid currently on screen lives under the active `<main>`. */
const SOURCE_GRID_SELECTOR = `#main-content ${GRID_SELECTOR}`;

/** Hides a real grid while the ghost stands in for it. */
const GRID_HIDDEN_CLASS = 'phase-grid-hidden';

export interface PhaseHandoverIncomingAttrs {
  id?: string;
  inert?: boolean;
  'aria-hidden'?: 'true';
}

export interface UsePhaseHandoverReturn {
  /** True from the phase flip until the word-building layer has finished entering. */
  active: Readonly<Ref<boolean>>;
  /** `<Transition name>`: swaps the whole class set for the handover pair. */
  transitionName: Readonly<Ref<string>>;
  /**
   * `<Transition mode>`: `undefined` during the handover so both layers are laid
   * out at once — that is what makes the destination grid measurable.
   */
  transitionMode: Readonly<Ref<'out-in' | undefined>>;
  /** Absolutely positions the two phase layers so they can overlap. */
  layerClass: Readonly<Ref<string | undefined>>;
  /**
   * Applied to the incoming word-building `<main>`. While it is invisible it
   * must not duplicate `id="main-content"` or expose a second `main` landmark.
   */
  incomingAttrs: Readonly<Ref<PhaseHandoverIncomingAttrs>>;
  ghostVisible: Readonly<Ref<boolean>>;
  ghostAnchor: Readonly<Ref<CSSProperties>>;
  ghostRef: Ref<HTMLElement | null>;
  onBeforeLeave: (el: Element) => void;
  onEnter: (el: Element) => void;
  onAfterLeave: () => void;
  onAfterEnter: () => void;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Shared-element handover for the `selecting-letters` → `in-progress` flip (#438).
 *
 * The letter grid is the shared element: instead of crossfading two screens, a
 * ghost copy of the grid travels from where the selection screen drew it to
 * where the word-building screen will, while the selection chrome steps back and
 * the round screen arrives underneath.
 *
 * The travelling element is a `position: fixed` ghost rather than the real grid
 * because the real one is trapped: it sits inside FitToHeight's `.fit-frame`,
 * which is `overflow: hidden` (deliberately — see #289), so translating it would
 * clip it. Measuring in viewport space via `getBoundingClientRect` also makes the
 * move immune to the two layers settling at different FitToHeight scales.
 *
 * Every other phase transition keeps the shipped `out-in` crossfade, and so does
 * this one under `prefers-reduced-motion`, where the handover never activates.
 */
export function usePhaseHandover(gameState: Ref<AppGameState>): UsePhaseHandoverReturn {
  const active = ref(false);
  const outgoingPresent = ref(false);
  const ghostVisible = ref(false);
  const ghostAnchor = ref<CSSProperties>({});
  const ghostRef = ref<HTMLElement | null>(null);

  let sourceEl: HTMLElement | null = null;
  let destEl: HTMLElement | null = null;
  let sourceRect: DOMRect | null = null;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let frames: number[] = [];
  let detachTravelEnd: (() => void) | null = null;

  const cancelFrames = () => {
    if (typeof cancelAnimationFrame === 'function') frames.forEach(cancelAnimationFrame);
    frames = [];
  };

  const revealRealGrids = () => {
    sourceEl?.classList.remove(GRID_HIDDEN_CLASS);
    destEl?.classList.remove(GRID_HIDDEN_CLASS);
    sourceEl = null;
    destEl = null;
    sourceRect = null;
  };

  /** Puts the ghost away and shows whichever real grids it was standing in for. */
  const abandonGhost = () => {
    cancelFrames();
    detachTravelEnd?.();
    detachTravelEnd = null;
    ghostVisible.value = false;
    ghostAnchor.value = {};
    revealRealGrids();
  };

  const clearWatchdog = () => {
    if (watchdog !== undefined) clearTimeout(watchdog);
    watchdog = undefined;
  };

  /**
   * The watchdog guards two independent endings — the arriving layer settling
   * and the ghost landing — so it is only stood down once both have happened.
   */
  const clearWatchdogIfSettled = () => {
    if (!active.value && !ghostVisible.value) clearWatchdog();
  };

  /**
   * Hands `id="main-content"` and interactivity to the arriving layer.
   *
   * Tied to the outgoing layer actually leaving, and to nothing else: while that
   * layer is mounted it holds the id and is the document's `main` landmark, so
   * releasing early would put two of each in the document at once. That includes
   * the ghost being put away early (nothing measurable to fly to) and the
   * handover being force-finished by the watchdog.
   */
  const releaseOutgoing = () => {
    outgoingPresent.value = false;
  };

  /**
   * Ends the handover outright: the ghost goes, the watchdog is stood down and
   * `name`/`mode` return to the shipped crossfade. Used by the watchdog, by a
   * phase change that lands mid-flight, and by teardown.
   *
   * Deliberately does not release the outgoing layer: `end` can run while that
   * layer is still on screen, and `onAfterLeave` is the only point at which it
   * is known to be gone.
   */
  const end = () => {
    abandonGhost();
    active.value = false;
    clearWatchdog();
  };

  const startTravel = (layer: Element) => {
    const ghost = ghostRef.value;
    const dest = layer.querySelector<HTMLElement>(GRID_SELECTOR);
    if (!ghost || !dest || !sourceRect) {
      abandonGhost();
      return;
    }

    const to = dest.getBoundingClientRect();
    if (to.width <= 0) {
      abandonGhost();
      return;
    }

    destEl = dest;
    dest.classList.add(GRID_HIDDEN_CLASS);

    const scale = to.width / sourceRect.width;
    const dx = to.left - sourceRect.left;
    const dy = to.top - sourceRect.top;

    // The ghost outlives the outgoing layer's leave transition: its travel only
    // starts once the measurement has settled, so it is still a few frames from
    // its destination when the layer below finishes leaving. Swapping it for the
    // real grid on its own transitionend keeps the move from being cut short.
    const onTravelEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'transform') return;
      abandonGhost();
      clearWatchdogIfSettled();
    };
    ghost.addEventListener('transitionend', onTravelEnd);
    detachTravelEnd = () => ghost.removeEventListener('transitionend', onTravelEnd);

    ghost.style.transition = `transform ${ANIMATION_CONFIG.PHASE_HANDOVER_TRAVEL_MS}ms var(--ease-dramatic)`;
    // Two distinct style computations, or the transform is applied with the
    // transition in one pass and the browser may jump straight to the end.
    void ghost.offsetHeight;
    ghost.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
  };

  /**
   * Takes the outgoing layer out of flow so the two screens overlap.
   *
   * This cannot be a class binding: the leaving element was rendered before the
   * handover activated, and Vue keeps that vnode's DOM untouched while it
   * transitions out. Left in flow it would stack above the arriving screen, and
   * the destination grid would be measured in the wrong place.
   */
  const onBeforeLeave = (el: Element) => {
    if (active.value) el.classList.add('phase-layer');
  };

  const onEnter = (el: Element) => {
    if (!active.value) return;

    // FitToHeight measures on `nextTick` and applies its scale on a following
    // frame (FitToHeight.vue), so the destination grid's rect is not final at
    // the moment the layer is inserted. Let both settle before measuring.
    void nextTick().then(() => {
      if (!active.value || typeof requestAnimationFrame !== 'function') {
        if (active.value) startTravel(el);
        return;
      }
      frames.push(
        requestAnimationFrame(() => {
          frames.push(
            requestAnimationFrame(() => {
              if (active.value) startTravel(el);
            })
          );
        })
      );
    });
  };

  /**
   * The outgoing layer is gone, so the arriving one takes over the screen.
   *
   * Deliberately not where the ghost ends: waiting for the enter transition
   * would leave a window with no element carrying `id="main-content"` at all,
   * and ending the ghost here would cut its travel short. The two are separate
   * events on separate elements.
   *
   * Not gated on `active`, because this is the only path that transfers the id:
   * a handover the watchdog force-finished still has an outgoing layer on its
   * way out, and the arriving one has to inherit the id when it goes.
   */
  const onAfterLeave = () => {
    releaseOutgoing();
  };

  /**
   * The arriving layer has settled, so the transition goes back to the shipped
   * crossfade.
   *
   * Deliberately not where the ghost ends: its travel only starts once the
   * measurement has settled, so it can still be in the air here. It is left to
   * its own transitionend, with the watchdog as the backstop.
   */
  const onAfterEnter = () => {
    if (!active.value) return;
    active.value = false;
    clearWatchdogIfSettled();
  };

  watch(
    gameState,
    (next, prev) => {
      // Any other phase change while a handover is in flight abandons it: the
      // layer it was flying towards is on its way out. `ghostVisible` is checked
      // too, since a ghost can outlive `active` by the frames it still has left
      // to travel.
      if (active.value || ghostVisible.value) end();

      if (prev !== 'selecting-letters' || next !== 'in-progress') return;
      if (prefersReducedMotion()) return;

      const source = document.querySelector<HTMLElement>(SOURCE_GRID_SELECTOR);
      if (!source) return;
      const from = source.getBoundingClientRect();
      if (from.width <= 0) return;

      sourceEl = source;
      sourceRect = from;
      source.classList.add(GRID_HIDDEN_CLASS);

      ghostAnchor.value = {
        left: `${from.left}px`,
        top: `${from.top}px`,
        width: `${from.width}px`,
      };
      ghostVisible.value = true;
      outgoingPresent.value = true;
      active.value = true;

      watchdog = setTimeout(end, ANIMATION_CONFIG.PHASE_HANDOVER_WATCHDOG_MS);
    },
    // Synchronous so the flag is set before the swap render: <Transition> reads
    // `mode` and resolves its hooks inside its render function, so a post-render
    // flush would leave this render on the shipped out-in path.
    { flush: 'sync' }
  );

  onScopeDispose(end);

  return {
    active,
    transitionName: computed(() => (active.value ? 'phase-handover' : 'game-state')),
    transitionMode: computed(() => (active.value ? undefined : 'out-in')),
    layerClass: computed(() => (active.value ? 'phase-layer' : undefined)),
    incomingAttrs: computed(() =>
      outgoingPresent.value
        ? { inert: true, 'aria-hidden': 'true' as const }
        : { id: 'main-content' }
    ),
    ghostVisible,
    ghostAnchor,
    ghostRef,
    onBeforeLeave,
    onEnter,
    onAfterLeave,
    onAfterEnter,
  };
}
